import { useState, useContext, useReducer, useEffect } from 'react';
import { useGet } from '@/query/fetchHooks';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '@/components/Contexts/AuthContext';
import {
  InputSelect,
  Label,
  Button,
  InputDate,
  Switch,
  IconButton,
  PersonAvatar,
} from '@teo/components';
import { CrossFilled } from '@teo/components/icons';
import { InputSelectOption } from '@/models/organisation.interface';
import { PaginationMultiSelect } from '@/components/PaginationMultiSelect';
import { PaginationSingleSelect } from '@/components/PaginationSingleSelect';
import DownloadReportModal from '../DownloadReportModal';
import ReportCoursesDataTable from '@/components/ReportDataTable/ReportCoursesDataTable';
import { languages } from '@/constants/languages';
import { parseDate } from '@/util';

const COURSE_OVERVIEW_VALUE = 'courseOverview';
const COURSE_OVERVIEW_NO_COMPETENCES_VALUE = 'courseOverviewNoCompetences';

const COURSE_OVERVIEW_COMPACT_VALUE = 'overviewCourse';
const COURSE_OVERVIEW_COMPACT_NO_COMPETENCES_VALUE =
  'overviewCourseNoCompetences';

const reportTypeList = [
  {
    value: COURSE_OVERVIEW_NO_COMPETENCES_VALUE,
    label: 'report.courseOverviewDetailed',
  },
  {
    value: COURSE_OVERVIEW_VALUE,
    label: 'report.courseOverviewDetailedCompetences',
  },
  {
    value: COURSE_OVERVIEW_COMPACT_NO_COMPETENCES_VALUE,
    label: 'report.courseOverview',
  },
  {
    value: COURSE_OVERVIEW_COMPACT_VALUE,
    label: 'report.courseOverviewCompetences',
  },
  {
    value: 'courseCsv',
    label: 'report.courseCsv',
  },
  {
    value: 'courseXlsx',
    label: 'report.courseXlsx',
  },
  {
    value: 'studentOverview',
    label: 'report.student_overview',
  },
  {
    value: 'coursedetailsCsv',
    label: 'report.coursedetailsCsv',
  },
  {
    value: 'coursedetailsXlsx',
    label: 'report.coursedetailsXlsx',
  },
  {
    value: 'certificate',
    label: 'report.certificate',
  },
  {
    value: 'aggregateLessonCsv',
    label: 'report.resultsCsv',
  },
  {
    value: 'aggregateLessonXlsx',
    label: 'report.resultsXlsx',
  },
  {
    value: 'exportResults',
    label: 'report.resultsPdf',
  },
];

type reportDownload = {
  coursesReport: any;
  usersReport: any;
  selectedLessons?: any;
  fromDate: any;
  untilDate: any;
  fileType: any;
  reportType: any;
  reportEmail: boolean;
  lang: any;
  noCompetences?: boolean;
};

function CoursesReport({ courseType }: any) {
  const { t } = useTranslation();
  const { auth } = useContext(AuthContext);
  const useCertificates = auth?.user?.useCertificates;

  const filteredReportTypeList = useCertificates
    ? reportTypeList
    : reportTypeList.filter((item) => item.value !== 'certificate');

  const [fromDate, setFromDate] = useState<Date | undefined>();
  const [untilDate, setUntilDate] = useState<Date | undefined>();
  const [selectedCourses, setSelectedCourses] = useState<any[]>([]);
  const [isOpenDownloadtModal, setIsOpenDownloadtModal] = useState(false);
  const [allCourses, setAllCourses] = useState(false);
  const [disabledAll, setDisabledAll] = useState(true);
  const [selectType, setSelectType] = useState(reportTypeList[0].value);
  const [allowMultiCourseSelect, setAllowMultiCourseSelect] = useState(true);
  const [allowLanguage, setAllowLanguage] = useState(true);
  const [allowDateRange, setAllowDateRange] = useState(true);
  const [enableLessonSelect, setEnableLessonSelect] = useState(false);
  const [selectedLessons, setSelectedLessons] = useState<any[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
  const [userSelect, setUserSelect] = useState<boolean>(false);
  const [studentSelect, setStudentSelect] = useState<boolean>(false);
  const [allUsers, setAllUsers] = useState(true);

  const reportState = {
    coursesReport: [],
    usersReport: [],
    fromDate,
    untilDate,
    reportType: {
      label: reportTypeList[0].label,
      value: reportTypeList[0].value,
    },
    reportEmail: true,
    lang: languages?.find((option) => option.value === auth?.user?.lang),
    noCompetences:
      reportTypeList[0].value === COURSE_OVERVIEW_NO_COMPETENCES_VALUE,
  };

  const [stateReport, updateStateReport] = useReducer(
    (state: reportDownload, updates: any) => ({ ...state, ...updates }),
    reportState
  );

  const isNoCompetencesReport =
    stateReport.reportType?.value === COURSE_OVERVIEW_NO_COMPETENCES_VALUE ||
    stateReport.reportType?.value ===
      COURSE_OVERVIEW_COMPACT_NO_COMPETENCES_VALUE;

  const getDownloadReportType = (value: string) => {
    if (value === COURSE_OVERVIEW_NO_COMPETENCES_VALUE) {
      return COURSE_OVERVIEW_VALUE;
    }

    if (value === COURSE_OVERVIEW_COMPACT_NO_COMPETENCES_VALUE) {
      return COURSE_OVERVIEW_COMPACT_VALUE;
    }

    return value;
  };

  const downloadStateReport = {
    ...stateReport,
    reportType: {
      ...stateReport.reportType,
      value: getDownloadReportType(stateReport.reportType?.value),
    },
    noCompetences: isNoCompetencesReport,
  };

  const getCourseId = (course: any) => course?.courseId ?? course?.id;
  const selectedCourse = selectedCourses?.[0];
  const selectedCourseId = getCourseId(selectedCourse);

  const handleAddCourses = async (newValue: any) => {
    const addCourse = [...stateReport.coursesReport];
    for (const obj2 of newValue) {
      if (obj2?.courseId || obj2?.id) {
        const existingCourses = stateReport.coursesReport.find(
          (obj1: any) =>
            (obj1?.courseId && obj1?.courseId === obj2?.courseId) ||
            (obj1?.id && obj1?.id === obj2?.id)
        );
        if (!existingCourses) {
          addCourse.push(obj2);
        }
      }
    }
    updateStateReport({ coursesReport: addCourse });
    setSelectedCourses([]);
  };

  const handleAddUsers = async (newValue: any) => {
    const addUser = [...(stateReport.usersReport || [])];
    for (const obj2 of newValue) {
      if (obj2?.id) {
        const existingTeam = (stateReport.usersReport || []).find(
          (obj1: any) => obj1?.id && obj1?.id === obj2?.id
        );
        if (!existingTeam) {
          addUser.push(obj2);
        }
      }
    }
    updateStateReport({ usersReport: addUser });
    setSelectedUsers([]);
  };

  const handleRemoveUsers = (id: any) => {
    const removeUser = stateReport.usersReport?.filter(
      (item: any) => item.id !== id
    );
    updateStateReport({ usersReport: removeUser });
  };

  const handleRemove = (courseId: any) => {
    const removeCourse = stateReport.coursesReport?.filter(
      (item: any) => getCourseId(item) !== courseId
    );
    updateStateReport({ coursesReport: removeCourse });
  };

  useEffect(() => {
    if (allUsers) {
      updateStateReport({ usersReport: [] });
    }
  }, [allUsers]);

  useEffect(() => {
    //defaults that works for the most reports
    setAllCourses(false);
    setDisabledAll(true);
    setAllowMultiCourseSelect(true);
    setAllowDateRange(true);
    setEnableLessonSelect(false);
    setUserSelect(false);
    setStudentSelect(false);
    setAllUsers(true);

    //reset data
    setSelectedUsers([]);
    setSelectedLessons([]);
    setSelectedCourses([]);

    //exceptions
    if (
      selectType == COURSE_OVERVIEW_VALUE ||
      selectType == COURSE_OVERVIEW_NO_COMPETENCES_VALUE ||
      selectType == COURSE_OVERVIEW_COMPACT_VALUE ||
      selectType == COURSE_OVERVIEW_COMPACT_NO_COMPETENCES_VALUE ||
      selectType == 'overviewCourse' ||
      selectType == 'studentOverview' ||
      selectType == 'certificate' ||
      selectType == 'aggregateLessonCsv' ||
      selectType == 'aggregateLessonXlsx'
    ) {
      setUserSelect(true);
    }
    if (selectType == 'exportResults') {
      setAllowMultiCourseSelect(false);
      setAllowDateRange(false);
      setStudentSelect(true);
    }
    if (
      selectType == 'aggregateLessonCsv' ||
      selectType == 'aggregateLessonXlsx'
    ) {
      setAllowMultiCourseSelect(false);
      setAllowDateRange(false);
      setEnableLessonSelect(true);
    }
    if (selectType.endsWith('Csv') || selectType.endsWith('Xlsx')) {
      setAllowLanguage(false);
    }
  }, [selectType]);

  return (
    <div className="gap- flex flex-col">
      <div className="mt-6 rounded-lg border border-grey-02 p-6">
        <div className="flex max-w-xl flex-col">
          <p className="pb-2 text-base font-semibold text-black">
            {t('pages.report_page.teams_tab.title_1')}
          </p>
          <div className="mb-1 mt-4 text-sm font-semibold text-black">
            <p className="mb-1">
              {t('pages.report_page.teams_tab.type_of_report')}
            </p>
            <div className="flex">
              <span className="w-full">
                <InputSelect<InputSelectOption>
                  value={stateReport.reportType}
                  onChange={(e: InputSelectOption) => {
                    const noCompetences =
                      e?.value === COURSE_OVERVIEW_NO_COMPETENCES_VALUE ||
                      e?.value === COURSE_OVERVIEW_COMPACT_NO_COMPETENCES_VALUE;

                    setSelectType(e?.value);
                    updateStateReport({
                      reportType: e,
                      noCompetences,
                    });
                  }}
                  options={filteredReportTypeList}
                >
                  {(options) => (
                    <span>{options.label && t(options.label)}</span>
                  )}
                </InputSelect>
              </span>
            </div>
          </div>
          {!disabledAll && (
            <div className="mt-5 flex flex-row items-center gap-4 rounded-lg border border-grey-02 p-4">
              <div data-testid="reportEmail">
                <Switch
                  checked={allCourses}
                  disabled={disabledAll}
                  onChange={(isCheck: any) => setAllCourses(isCheck)}
                />
              </div>
              <div>
                <h4 className="font-normal">
                  {t(`pages.report_page.select_${courseType}`)}
                </h4>
              </div>
            </div>
          )}
          {!allCourses && (
            <div className="z-[1] mb-1 mt-4 text-sm font-semibold text-black">
              <p className="mb-1">{t(`pages.report_page.${courseType}`)}</p>
              <div className="relative z-[2] flex">
                <span className="w-full">
                  {allowMultiCourseSelect ? (
                    <PaginationMultiSelect
                      id="teams-report"
                      value={selectedCourses}
                      onChange={(value: any) => {
                        setSelectedCourses(value);
                      }}
                      displayValue={(el: any) => {
                        if (!el || el?.length === 0) return '';
                        return (
                          ((el?.courseName ? el?.courseName : el?.name) || '') +
                          ` (${parseDate(
                            el?.courseStartDate || el?.createdAt
                          )})`
                        );
                      }}
                      keyFn={(row: any) => getCourseId(row)}
                      fetch={useGet.bind(
                        null,
                        `/organisationcoursedetails?organisationId=${auth?.user?.activeOrganisationId}&courseType=${courseType}`
                      )}
                      defaultSort={[{ id: 'courseName', desc: false }]}
                      render={(row: any) => {
                        return (
                          <div className="flex justify-between">
                            <span>
                              {row?.courseName ? row?.courseName : row?.name}
                            </span>
                            <span>{`${parseDate(
                              row?.courseStartDate || row?.createdAt
                            )}`}</span>
                          </div>
                        );
                      }}
                    />
                  ) : (
                    <PaginationSingleSelect
                      id="teams-report"
                      value={selectedCourses?.[0]}
                      onChange={(value: any) => {
                        setSelectedLessons([]);
                        setSelectedCourses([value]);
                      }}
                      keyFn={(row: any) => getCourseId(row)}
                      fetch={useGet.bind(
                        null,
                        `/organisationcoursedetails?organisationId=${auth?.user?.activeOrganisationId}&courseType=${courseType}`
                      )}
                      displayValue={(el: any) => {
                        if (!el || el?.length === 0) return '';
                        return (
                          ((el?.courseName ? el?.courseName : el?.name) || '') +
                          ` (${parseDate(
                            el?.courseStartDate || el?.createdAt
                          )})`
                        );
                      }}
                      defaultSort={[{ id: 'courseName', desc: false }]}
                      render={(row: any) => {
                        return (
                          <div className="flex justify-between">
                            <span>
                              {row?.courseName ? row?.courseName : row?.name}
                            </span>
                            <span>{`${parseDate(
                              row?.courseStartDate || row?.createdAt
                            )}`}</span>
                          </div>
                        );
                      }}
                    />
                  )}
                </span>
                {allowMultiCourseSelect && (
                  <Button
                    className="ml-3 h-[36px] text-base font-medium"
                    variant="primary"
                    disabled={selectedCourses.length === 0}
                    onClick={async () => {
                      handleAddCourses(selectedCourses);
                    }}
                  >
                    {t('button.add_btn')}
                  </Button>
                )}
              </div>
              {stateReport?.coursesReport?.length > 0 && (
                <div className="mt-6">
                  {stateReport?.coursesReport?.map((course: any) => (
                    <div
                      key={getCourseId(course) ?? course.username}
                      className="mb-4 flex items-center justify-between"
                    >
                      <div className="flex w-full items-center justify-between">
                        <div className="flex items-center">
                          <div className="mr-3 w-16 min-w-16 sm:w-20 sm:min-w-20">
                            <img
                              src={
                                course?.courseImage ||
                                course?.image ||
                                '/images/courseFallback.png'
                              }
                              alt={course?.courseName || course?.name || ''}
                              className="inline h-12 w-full rounded-lg object-cover sm:h-14"
                            />
                          </div>
                          {course?.courseName || course?.name}
                        </div>
                        <div>{parseDate(course?.createdAt)}</div>
                      </div>
                      <div data-testid="daleteItem" className="my-auto ml-6">
                        <IconButton
                          Icon={CrossFilled}
                          variant="ghost"
                          onClick={() => handleRemove(getCourseId(course))}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {(userSelect || (studentSelect && !!selectedCourseId)) && (
                <>
                  <div className="z-[1] mb-1 mt-4 text-sm font-semibold text-black">
                    <p className="mb-1">
                      {t('pages.report_page.user_tab.users')}
                    </p>
                    <div className="mt-2 flex flex-row items-center gap-4 rounded-lg border border-grey-02 p-4">
                      <div data-testid="reportEmail">
                        <Switch
                          checked={allUsers}
                          //disabled={disabledAll}
                          onChange={(isCheck) => setAllUsers(isCheck)}
                        />
                      </div>
                      <div>
                        <h4 className="font-normal">
                          {t('pages.report_page.select_users')}
                        </h4>
                      </div>
                    </div>
                    {!allUsers && (
                      <>
                        <div className="mt-4 flex">
                          <span className="w-full">
                            <PaginationMultiSelect
                              id="teams-report"
                              value={selectedUsers}
                              onChange={(value: any) => {
                                setSelectedUsers(value);
                              }}
                              keyFn={(row: any) => row?.id}
                              fetch={useGet.bind(
                                null,
                                `/memberdetails?organisationId=${auth?.user?.activeOrganisationId}`
                                // studentSelect
                                //   ? `/mystudents?organisationId=${auth?.user?.activeOrganisationId}&userId=${auth?.user?.id}&courseId=${selectedCourseId}`
                                //   : `/memberdetails?organisationId=${auth?.user?.activeOrganisationId}`
                              )}
                              defaultSort={[{ id: 'username', desc: false }]}
                              render={(row: any) => {
                                return row?.username;
                              }}
                            />
                          </span>
                          <Button
                            className="ml-3 h-[36px] text-base font-medium"
                            variant="primary"
                            disabled={selectedUsers.length === 0}
                            onClick={async () => {
                              handleAddUsers(selectedUsers);
                            }}
                          >
                            {t('button.add_btn')}
                          </Button>
                        </div>
                        {stateReport?.usersReport?.length > 0 && (
                          <div className="mt-6">
                            {stateReport?.usersReport?.map((user: any) => (
                              <div
                                key={user.id == null ? user.username : user.id}
                                className="mb-4 flex items-center justify-between"
                              >
                                <div className="flex items-center">
                                  <PersonAvatar
                                    inline
                                    id={user.id}
                                    name={user.username}
                                    imageSrc={user.picture}
                                  ></PersonAvatar>
                                  <p className="text-[12px] font-medium text-[#231f20] sm:text-sm">
                                    {user.username}
                                  </p>
                                </div>
                                <div
                                  data-testid="daleteItem"
                                  className="my-auto ml-6"
                                >
                                  <IconButton
                                    Icon={CrossFilled}
                                    variant="ghost"
                                    onClick={() => handleRemoveUsers(user.id)}
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
          {enableLessonSelect && selectedCourses.length > 0 && (
            <div className="mb-1 mt-4 text-sm font-semibold text-black">
              <p className="mb-1">
                {t(`pages.create_library.lesson`)} ({t('forms.optional')})
              </p>
              <div className="flex">
                <span className="w-full">
                  <PaginationSingleSelect
                    id="teams-report"
                    value={selectedLessons?.[0]}
                    onChange={(value: any) => {
                      setSelectedLessons([value]);
                    }}
                    keyFn={(row: any) => row?.id}
                    fetch={useGet.bind(
                      null,
                      `/usedcourselessonassets?organisationId=${auth?.user?.activeOrganisationId}&coursecontentId=${selectedCourse?.coursecontentId}&courseId=${selectedCourseId}`
                    )}
                    defaultSort={[{ id: 'name', desc: false }]}
                    render={(row: any) => {
                      return row?.name;
                    }}
                  />
                </span>
              </div>
            </div>
          )}
          {allowLanguage && (
            <div className="mt-3">
              <Label htmlFor="language">
                {t('pages.profile_account.language')}
              </Label>
              <div id="language">
                <InputSelect<InputSelectOption>
                  value={stateReport.lang}
                  onChange={async (language) => {
                    updateStateReport({ lang: language });
                  }}
                  options={languages}
                >
                  {(options) => t(options.label)}
                </InputSelect>
              </div>
            </div>
          )}
          {allowDateRange && (
            <div className="mt-7 flex flex-col gap-5 pr-2 sm:flex-row lg:pr-0">
              <div className="basis-1/2" data-testid="dateStart">
                <Label htmlFor="dateStart">
                  {t('pages.report_page.teams_tab.from')}
                </Label>
                <InputDate
                  id="dateStart"
                  onChange={(date) => updateStateReport({ fromDate: date })}
                  value={stateReport.fromDate}
                  variant="date"
                />
              </div>
              <div className="basis-1/2" data-testid="dateEnd">
                <Label htmlFor="dateEnd">
                  {t('pages.report_page.teams_tab.until')}
                </Label>
                <InputDate
                  id="dateEnd"
                  onChange={(date) => updateStateReport({ untilDate: date })}
                  value={stateReport.untilDate}
                  variant="date"
                />
              </div>
            </div>
          )}
          <div className="mt-5 flex flex-row items-center gap-4 rounded-lg border border-grey-02 p-4">
            <div data-testid="reportEmail">
              <Switch
                checked={stateReport.reportEmail}
                onChange={(isCheck) =>
                  updateStateReport({ reportEmail: isCheck })
                }
              />
            </div>
            <div>
              <h4 className="pb-2 font-normal">
                {t('pages.report_page.teams_tab.toggle_email_heading')}
              </h4>
              <p className="text-xs text-grey-05">
                {t('pages.report_page.teams_tab.toggle_email_text')}
              </p>
            </div>
          </div>
          <div className="flex pt-6">
            <Button
              onClick={() => {
                if (!allowMultiCourseSelect) {
                  updateStateReport({ coursesReport: selectedCourses });
                }
                if (enableLessonSelect) {
                  updateStateReport({ selectedLessons: selectedLessons });
                } else {
                  updateStateReport({ selectedLessons: undefined });
                }
                document.body.style.overflow = 'hidden';
                setIsOpenDownloadtModal(true);
              }}
              disabled={
                stateReport?.coursesReport?.length < 1 &&
                !allCourses &&
                !(!allowMultiCourseSelect && selectedCourses.length > 0)
              }
            >
              {t('pages.report_page.teams_tab.btn_download')}
            </Button>
          </div>
        </div>
      </div>

      <ReportCoursesDataTable
        reportTypeList={reportTypeList}
        courseType={courseType}
      />

      {isOpenDownloadtModal && (
        <DownloadReportModal
          report={'courses'}
          isOpenDownloadtModal={isOpenDownloadtModal}
          closeModal={() => {
            document.body.style.overflow = 'unset';
            setIsOpenDownloadtModal(false);
          }}
          setIsOpenDownloadtModal={setIsOpenDownloadtModal}
          stateReport={downloadStateReport}
          user={auth?.user}
          updateStateReport={updateStateReport}
          allSelect={allCourses ? undefined : true}
          courseType={courseType}
        />
      )}
    </div>
  );
}

export default CoursesReport;
